// react
import { ChangeEvent, MouseEvent, useContext, useEffect, useState } from 'react';

// types
import { Document, ErrorCause, Feedback, Program, ProgramPayload } from '../../../@types';

// router
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// fields
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';

// pages
import { Preview } from './Preview/Preview';

// utils
import { dataUrlToFile } from '../../../utils/files';

// props
export interface FormEditProgramValues {
	description: string;
	hero: File[];
	logo: File[];
	name: string;
	shortName: string;
}

export interface FormEditProgramProps {
	onSuccess?: () => void;
}

export const FormEditProgram = ({ onSuccess }: FormEditProgramProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [program, setProgram] = useState<Program | null>(null);
	const [values, setValues] = useState<FormEditProgramValues>({
		description: '',
		hero: [],
		logo: [],
		name: '',
		shortName: '',
	});

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// fetch program
	const { isLoading: isLoadingProgram, refetch: refetchProgram } = useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/programs?project=${project?.id}&populate=hero,logo`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.length) {
				setProgram(res.data[0]);
			}
		},
	});

	// vars
	const idForm = `form-edit-program`;

	// handlers
	const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		setValues((valuesPrev) => {
			return {
				...valuesPrev,
				[name]: value,
			};
		});
	};

	const handleRemoveFile = (name: string) => {
		setValues((valuesPrev) => {
			return {
				...valuesPrev,
				[name]: [],
			};
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			const docsByName = [];
			const filesToUpload: { file: File; name: string }[] = [];

			if (values.hero.length) {
				filesToUpload.push({
					file: values.hero[0],
					name: 'hero',
				});
			}

			if (values.logo.length) {
				filesToUpload.push({
					file: values.logo[0],
					name: 'logo',
				});
			}

			// upload each file
			for (let i = 0; i < filesToUpload.length; i++) {
				// define form data
				const formData = new FormData();

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/program/${filesToUpload[i].name}/${new Date().getTime()}/`
				);
				formData.append('file', filesToUpload[i].file);

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
					method: 'post',
					body: formData,
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					throw new Error(resPostDoc.error.message, {
						cause: {
							id: resPostDoc.error.id,
						},
					});
				}

				if (resPostDoc.data) {
					docsByName.push({
						...filesToUpload[i],
						document: resPostDoc.data,
					});
				}
			}

			const body: Partial<ProgramPayload> = {
				description: values.description,
				hero: null,
				logo: null,
				name: values.name,
				shortName: values.shortName,
			};

			// add hero and logo document ids to body
			for (let i = 0; i < docsByName.length; i++) {
				if (docsByName[i].name === 'hero') {
					body.hero = docsByName[i].document.id;
				} else if (docsByName[i].name === 'logo') {
					body.logo = docsByName[i].document.id;
				}
			}

			let method = 'PATCH';
			let postOrPatchProgramUrl = `${process.env.REACT_APP_API_URL}/programs`;

			if (program) {
				postOrPatchProgramUrl += `/${program.id}`;
			} else {
				method = 'POST';
				body.project = project?.id;
			}

			const fetchPostProgram = await fetch(postOrPatchProgramUrl, {
				method,
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});

			const resPostProgram = await fetchPostProgram.json();

			if (resPostProgram.error) {
				throw new Error(resPostProgram.error.message, {
					cause: {
						id: resPostProgram.error.id,
						details: resPostProgram.error?.details,
					},
				});
			}

			// refetch
			refetchProgram();

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.programUpdated', { name: values.name }),
				severity: 'success',
			});
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'DOCUMENT_DELETE_FAILED') {
					feedbackNew.alert.children = t('error.documentDeleteFailed');
				}

				if (cause.id === 'PROGRAM_UNAUTHORIZED_PATCH') {
					feedbackNew.alert.children = t('error.unauthorized');
				}

				if (cause.id === 'PROGRAM_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	// update values when program changes
	useEffect(() => {
		if (program) {
			const heroFiles: File[] = [];
			const logoFiles: File[] = [];

			// create hero file
			if (program.hero) {
				const heroDoc = program.hero as Document;

				const heroFile = dataUrlToFile({
					dataUrl: `data:${heroDoc.metadata.type};base64,${heroDoc.blob}`,
					fileName: heroDoc.name,
				});

				heroFiles.push(heroFile);
			}

			// create logo file
			if (program.logo) {
				const logoDoc = program.logo as Document;

				const logoFile = dataUrlToFile({
					dataUrl: `data:${logoDoc.metadata.type};base64,${logoDoc.blob}`,
					fileName: logoDoc.name,
				});

				logoFiles.push(logoFile);
			}

			setValues((valuesPrev) => {
				return {
					...valuesPrev,
					description: program.description || '',
					hero: heroFiles,
					logo: logoFiles,
					name: program.name,
					shortName: program.shortName || '',
				};
			});
		}
	}, [program]);

	return (
		<form id={idForm} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} lg={4}>
					<Paper
						sx={(theme) => {
							return {
								padding: theme.spacing(2),
								height: '100%',
								minHeight: {
									lg: 400,
								},
							};
						}}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="h3">{t('images')}</Typography>
							</Grid>
							<Grid item xs={12}>
								<FilePicker
									accept="image/*"
									helperText={t('recommendedSize', { width: 600, height: 600 })}
									id={`${idForm}-logo`}
									label={t('logo')}
									multiple={false}
									name="logo"
									onChange={(e, filesSelected) => {
										setValues((valuesPrev) => {
											return {
												...valuesPrev,
												logo: filesSelected,
											};
										});
									}}
									onRemove={() => handleRemoveFile('logo')}
									orientation="horizontal"
									value={values.logo}
								/>
							</Grid>
							<Grid item xs={12}>
								<FilePicker
									accept="image/*"
									helperText={t('recommendedSize', { width: 800, height: 1200 })}
									id={`${idForm}-hero`}
									label={t('hero')}
									multiple={false}
									name="hero"
									onChange={(e, filesSelected) => {
										setValues((valuesPrev) => {
											return {
												...valuesPrev,
												hero: filesSelected,
											};
										});
									}}
									onRemove={() => handleRemoveFile('hero')}
									orientation="horizontal"
									value={values.hero}
								/>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
				<Grid item xs={12} lg={4}>
					<Paper
						sx={(theme) => {
							return {
								padding: theme.spacing(2),
								height: '100%',
							};
						}}>
						<Grid
							alignItems="stretch"
							container
							direction="column"
							spacing={2}
							sx={{ height: 'calc(100% + 16px)' }}>
							<Grid item>
								<Typography variant="h3">{t('details')}</Typography>
							</Grid>
							<Grid item flex={1}>
								<Grid
									container
									direction="column"
									justifyContent="space-between"
									spacing={2}
									sx={{ height: 'calc(100% + 16px)' }}>
									<Grid item>
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<Input
													id={`${idForm}-name`}
													label={t('name')}
													name="name"
													onChange={handleChangeValue}
													required={true}
													value={values.name}
												/>
											</Grid>
											<Grid item xs={12}>
												<Input
													id={`${idForm}-short-name`}
													label={t('shortName')}
													name="shortName"
													onChange={handleChangeValue}
													value={values.shortName}
												/>
											</Grid>
											<Grid item xs={12}>
												<Input
													id={`${idForm}-description`}
													label={t('description')}
													maxLength={200}
													minRows={4}
													multiline={true}
													onChange={handleChangeValue}
													name="description"
													value={values.description}
												/>
											</Grid>
											{feedback && (
												<Grid item xs={12}>
													<Alert severity="error" {...feedback.alert}>
														{feedback.title && <AlertTitle {...feedback.title} />}
														{feedback.alert.children}
													</Alert>
												</Grid>
											)}
										</Grid>
									</Grid>

									<Grid item>
										<Grid container justifyContent="flex-end" spacing={2}>
											<Grid item xs={12}>
												<LoadingButton
													fullWidth={true}
													loading={isLoading}
													type="submit"
													variant="contained">
													{t('saveProgram')}
												</LoadingButton>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
				<Grid item xs={12} lg={4}>
					<Preview isLoadingProgram={isLoadingProgram} values={values} />
				</Grid>
			</Grid>
		</form>
	);
};
